import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { compose, bindActionCreators } from 'redux';
import { updateDocumentScoringCategory } from '../actionCreators';
import { loadDocumentScoringCategoryOptions } from 'actions/actionCreators/dictionariesActions';
import { isMandatory } from 'utils/simple-validations';
import { connect } from 'react-redux';
import * as editableDocument from 'utils/model/editableDocument';
import flow from 'lodash.flow';
import { collection } from 'utils/immutable';
import { FakeText } from '@totalsoft/rocket-ui';
import { when, both, propChanged, prop, isNumber } from 'utils/fn-combinators';

//inner components
import DocumentScoringCategoryListComponent from './DocumentScoringCategoryListComponent';

const getItemValidationRules = () => ({
  option: [when(prop('isMandatory'), isMandatory())]
});

const findCategoryOptionByValue = (categoryOptions, scoringCategoryId, value) =>
  categoryOptions.find(option => option.scoringCategoryId === scoringCategoryId && option.minValue <= value && option.maxValue >= value);

const getItemBusinessRules = ({ categoryOptions }) => ({
  value: when(both(propChanged('option'), isNumber(prop('option.minValue'))), prop('option.minValue')),
  option: when(propChanged('value'), category => findCategoryOptionByValue(categoryOptions, category.scoringCategoryId, category.value)),
  score: when(both(propChanged('option'), isNumber(prop('option.score'))), prop('option.score'))
});

class DocumentScoringCategoryListContainer extends PureComponent {
  state = {
    loading: false,
    saving: false,
    prevPropsCategoryList: null,
    categoryList: null
  };

  static getDerivedStateFromProps(props, state) {
    if (props.categoryList !== state.prevPropsCategoryList) {
      return {
        prevPropsCategoryList: props.categoryList,
        categoryList: props.categoryList.map(editableDocument.create)
      };
    }
    return null;
  }

  componentDidMount() {
    this.loadCategoryOptions();
  }

  loadCategoryOptions = () => {
    this.props.actions.loadDocumentScoringCategoryOptions();
  };

  componentDidUpdate(prevProps) {
    if (prevProps.categoryOptions !== this.props.categoryOptions) {
      this.loadCategoryOptions();
    }
  }

  changeItemProperty = (index, prop, value) => {
    this.setState(prevState => ({
      categoryList: collection.update(
        index,
        flow(editableDocument.update(prop, value, getItemBusinessRules(this.props)), editableDocument.validate(null, true)),
        prevState.categoryList
      )
    }));
  };

  save = async () => {
    const categoryList = this.state.categoryList.map(editableDocument.validate(getItemValidationRules(), false));

    if (categoryList.some(x => !x.validationInfo.isValid)) {
      this.setState({
        categoryList
      });
      return;
    }

    this.setState({
      categoryList,
      saving: true
    });

    try {
      const event = await this.props.actions.updateDocumentScoringCategory(
        this.props.documentId,
        this.props.siteId,
        this.props.documentScoringGridId,
        this.props.scoringClassificationId,
        this.props.entityType,
        categoryList.map(x => x.document)
      );
      if (event.ok) {
        this.setState(prevState => ({
          saving: false,
          categoryList: prevState.categoryList.map(flow(editableDocument.resetDirtyInfo, editableDocument.resetValidationInfo))
        }));
      } else {
        this.setState({ saving: false });
      }
    } catch (error) {
      this.setState({ saving: false });
    }
  };

  render() {
    const { categoryOptions, disabled, isHistory, viewScoringDetailLimits, scoringGridCategoryScoreRead } = this.props;
    const { categoryList, saving, loading } = this.state;
    return loading || !categoryList ? (
      <FakeText lines={6} />
    ) : (
      <DocumentScoringCategoryListComponent
        onPropertyItemChange={this.changeItemProperty}
        onSave={this.save}
        saving={saving}
        categoryOptions={categoryOptions}
        categoryList={categoryList}
        disabled={disabled}
        isHistory={isHistory}
        viewScoringDetailLimits={viewScoringDetailLimits}
        scoringGridCategoryScoreRead={scoringGridCategoryScoreRead}
      />
    );
  }
}

function mapStateToProps(state) {
  return {
    categoryOptions: state.dictionaries.scoringCategoryOptions.isLoaded ? state.dictionaries.scoringCategoryOptions.items : []
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        updateDocumentScoringCategory,
        loadDocumentScoringCategoryOptions
      },
      dispatch
    )
  };
}

DocumentScoringCategoryListContainer.propTypes = {
  documentId: PropTypes.number.isRequired,
  siteId: PropTypes.number.isRequired,
  documentScoringGridId: PropTypes.number,
  scoringClassificationId: PropTypes.number.isRequired,
  categoryList: PropTypes.array.isRequired,
  categoryOptions: PropTypes.array.isRequired,
  actions: PropTypes.object.isRequired,
  disabled: PropTypes.bool.isRequired,
  entityType: PropTypes.string.isRequired,
  isHistory: PropTypes.bool,
  viewScoringDetailLimits: PropTypes.bool.isRequired,
  scoringGridCategoryScoreRead: PropTypes.bool.isRequired
};

export default compose(connect(mapStateToProps, mapDispatchToProps))(DocumentScoringCategoryListContainer);
